import React from 'react';
import { Box, Typography } from '@mui/material';

// Components
import CreditChip from './CreditChip.js';
import PeriodTable from './PeriodTable.js';

// Utils
import { getSeasonName } from '../utils/dateUtils.js';
import { getSemesterChipColor } from '../utils/colorUtils.js';
import { CREDIT_LIMITS } from '../utils/constants.js';

/**
 * SemesterSection component for displaying a complete semester with header and period tables
 * @param {string} semesterKey - Unique key for this semester
 * @param {Object} semesterInfo - Semester information and groups
 * @param {number} semesterCredits - Credits for this semester
 * @param {Object} groupedCourses - All grouped courses
 * @param {Object} periodCredits - Credits per period
 * @param {Object} minimizedPeriods - Which periods are minimized
 * @param {Function} onTogglePeriodMinimize - Function to toggle period minimize
 * @param {Object} sensors - Drag and drop sensors
 * @param {Function} onDragEnd - Function to handle drag end
 * @param {Set} selectedIds - Set of selected course IDs
 * @param {Set} backupIds - Set of backup course IDs
 * @param {Set} selectedCourses - Set of selected course codes
 * @param {Function} onToggleSelect - Function to handle course selection
 * @param {Function} onAddCourse - Function to open add course dialog
 * @param {Object} allSemesterCredits - All semester credits for validation
 */
const SemesterSection = ({
  semesterKey,
  semesterInfo,
  semesterCredits,
  groupedCourses,
  periodCredits,
  minimizedPeriods,
  onTogglePeriodMinimize,
  sensors,
  onDragEnd,
  selectedIds,
  backupIds,
  selectedCourses,
  onToggleSelect,
  onAddCourse,
  allSemesterCredits
}) => {
  return (
    <Box key={semesterKey}>
      {/* Semester Header with Credit Counter */}
      <Box display="flex" alignItems="center" gap={2} mb={1}>
        <Typography variant="h5">
          {getSeasonName(semesterInfo.semester, semesterInfo.year)}
        </Typography>
        <CreditChip
          credits={semesterCredits}
          maxCredits={CREDIT_LIMITS.SEMESTER_OPTIMAL}
          type="semester"
          getColorFn={getSemesterChipColor}
          variant={semesterCredits > 0 ? 'filled' : 'outlined'}
          size="medium"
        />
      </Box>

      {/* Period Groups within this Semester */}
      {semesterInfo.groups.map((groupKey) => {
        const group = groupedCourses[groupKey];
        const periodCred = periodCredits[groupKey] || 0;
        const isMinimized = minimizedPeriods[groupKey] || false;

        return (
          <PeriodTable
            key={groupKey}
            groupKey={groupKey}
            group={group}
            periodCredits={periodCred}
            isMinimized={isMinimized}
            onToggleMinimize={onTogglePeriodMinimize}
            sensors={sensors}
            onDragEnd={onDragEnd}
            selectedIds={selectedIds}
            backupIds={backupIds}
            selectedCourses={selectedCourses}
            onToggleSelect={onToggleSelect}
            onAddCourse={onAddCourse}
            groupedCourses={groupedCourses}
            semesterCredits={allSemesterCredits}
          />
        );
      })}
    </Box>
  );
};

export default SemesterSection; 